import React, { useCallback, useMemo, useState } from "react";
import { Button, Collapsible, Spinner, Text } from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import Accordion from "components/Accordion";
import CompletedIMG from "img/illustrations/mascots/userGuide-complete.svg";
import AllDoneIMG from "img/illustrations/mascots/parrot_gift.png";
import StartGuideStep from "./StartGuideStep";
import {
  DEFAULT_MINUTES_PER_TASK,
  computeProgress,
  countTasksLeft,
  normalizeSteps,
  resolveText,
} from "./utils";
import "./StartGuide.sass";

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 1V15M1 8H15"
      stroke="#2E2E2E"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MinusIcon = () => (
  <svg width="16" height="2" viewBox="0 0 16 2" fill="none">
    <path
      d="M1 1H15"
      stroke="#2E2E2E"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronIcon = ({ flipped }) => (
  <svg
    width="21"
    height="20"
    viewBox="0 0 21 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: flipped ? "rotate(180deg)" : "none", marginTop: 3 }}
  >
    <path
      d="M14.6666 7.64174C14.5104 7.48653 14.2992 7.39941 14.0791 7.39941C13.8589 7.39941 13.6477 7.48653 13.4916 7.64174L10.4999 10.5917L7.54992 7.64174C7.39378 7.48653 7.18257 7.39941 6.96242 7.39941C6.74226 7.39941 6.53105 7.48653 6.37492 7.64174C6.29681 7.71921 6.23481 7.81138 6.19251 7.91293C6.1502 8.01448 6.12842 8.1234 6.12842 8.23341C6.12842 8.34342 6.1502 8.45234 6.19251 8.55389C6.23481 8.65544 6.29681 8.74761 6.37492 8.82507L9.90825 12.3584C9.98572 12.4365 10.0779 12.4985 10.1794 12.5408C10.281 12.5831 10.3899 12.6049 10.4999 12.6049C10.6099 12.6049 10.7188 12.5831 10.8204 12.5408C10.9219 12.4985 11.0141 12.4365 11.0916 12.3584L14.6666 8.82507C14.7447 8.74761 14.8067 8.65544 14.849 8.55389C14.8913 8.45234 14.9131 8.34342 14.9131 8.23341C14.9131 8.1234 14.8913 8.01448 14.849 7.91293C14.8067 7.81138 14.7447 7.71921 14.6666 7.64174Z"
      fill="#707070"
    />
  </svg>
);

const DEFAULT_TEXTS = {
  subtitle: "",
  allDone: "You've completed all setup tasks!",
  showPreviousTasks: "Show previous tasks",
  tasksLeft: ({ tasksLeft, minutesLeft }) =>
    `${tasksLeft} tasks left (About ${minutesLeft} minutes)`,
};

export default function StartGuide({
  steps,
  stepsContent,
  progress,
  loading = false,
  visible = true,
  texts,
  minutesPerTask = DEFAULT_MINUTES_PER_TASK,
  renderInline = true,
}) {
  const navigate = useNavigate();
  const [isPreviousTasksOpened, setIsPreviousTasksOpened] = useState(false);
  const [openStepKey, setOpenStepKey] = useState(null);

  const resolvedTexts = useMemo(() => ({ ...DEFAULT_TEXTS, ...texts }), [texts]);

  const resolvedSteps = useMemo(
    () => normalizeSteps(steps, stepsContent),
    [steps, stepsContent]
  );

  const resolvedProgress =
    typeof progress === "number" ? progress : computeProgress(resolvedSteps);

  const tasksLeft = countTasksLeft(resolvedSteps);
  const minutesLeft = tasksLeft * minutesPerTask;

  const headerText = resolveText(resolvedTexts.tasksLeft, {
    tasksLeft,
    minutesLeft,
  });

  const handleAction = useCallback(
    (action, event) => {
      event?.stopPropagation?.();
      event?.preventDefault?.();

      if (typeof action?.onClick === "function") {
        action.onClick({ navigate });
        return;
      }

      if (action?.href) {
        window.open(action.href, "_blank", "noopener,noreferrer");
        return;
      }

      if (action?.to) navigate(action.to);
    },
    [navigate]
  );

  const handleStepToggle = useCallback((stepKey) => {
    setOpenStepKey((prevKey) => (prevKey === stepKey ? null : stepKey));
  }, []);

  const renderStepHeader = useCallback(
    (step) => (open) => (
      <>
        <div className="StartGuide__itemHeaderLeft">
          {open ? (
            <div className="StartGuide__minusFix">
              <MinusIcon />
            </div>
          ) : (
            <PlusIcon />
          )}
          <Text fontWeight="bold" variant="bodyLg">
            <span
              className={`StartGuide__stepName${
                step.complete ? " is-complete" : ""
              }`}
            >
              {step.name}
            </span>
          </Text>
        </div>

        <div className="StartGuide__itemHeaderRight">
          {!step.complete &&
            (step.content.actions || []).map((action, index) => (
              <Button
                key={action.id || index}
                size="slim"
                variant={action.variant}
                onClick={(event) => handleAction(action, event)}
              >
                {action.label}
              </Button>
            ))}
          {step.complete && <img src={CompletedIMG} alt="completed" />}
        </div>
      </>
    ),
    [handleAction]
  );

  const renderSteps = useCallback(
    (items) => (
      <div className="StartGuide__list">
        {items.map((step) => (
          <Accordion
            key={step.key}
            content={
              <StartGuideStep
                content={step.content}
                complete={step.complete}
                onAction={handleAction}
              />
            }
            open={openStepKey === step.key}
            onToggle={() => handleStepToggle(step.key)}
            styles={{ wrapper: { borderRadius: 0 } }}
            renderCustomHeader={renderStepHeader(step)}
          />
        ))}
      </div>
    ),
    [handleAction, handleStepToggle, openStepKey, renderStepHeader]
  );

  if (!visible) return null;

  const uncompletedSteps = resolvedSteps.filter((step) => !step.complete);
  const completedSteps = resolvedSteps.filter((step) => step.complete);

  const content = (
    <div className="StartGuide">
      <div className="StartGuide__head">
        {!loading && resolvedProgress !== 100 && (
          <Text color="subdued">{headerText}</Text>
        )}
      </div>

      {loading && (
        <div className="StartGuide__spinner">
          <Spinner />
        </div>
      )}

      {!loading && (
        <div className="StartGuide__body">
          {resolvedTexts.subtitle && (
            <Text color="subdued">{resolvedTexts.subtitle}</Text>
          )}

          <div className="StartGuide__progress">
            <div className="StartGuide__progressBar">
              <div
                className="StartGuide__progressLine"
                style={{ width: `${resolvedProgress}%` }}
              />
            </div>
            <Text fontWeight="bold">{resolvedProgress}%</Text>
          </div>

          {resolvedProgress === 100 && (
            <div className="StartGuide__allDone">
              <img
                className="StartGuide__allDoneImage"
                src={AllDoneIMG}
                alt="completed user guide"
              />
              <Text>{resolvedTexts.allDone}</Text>
            </div>
          )}

          {renderSteps(uncompletedSteps)}

          {completedSteps.length > 0 && (
            <>
              <Button
                variant="plain"
                onClick={() => setIsPreviousTasksOpened(!isPreviousTasksOpened)}
                icon={<ChevronIcon flipped={isPreviousTasksOpened} />}
              >
                <Text color="subdued">{resolvedTexts.showPreviousTasks}</Text>
              </Button>

              <Collapsible
                open={isPreviousTasksOpened}
                id="start-guide-previous-tasks"
                transition={{ duration: "300ms", timingFunction: "ease-in-out" }}
                expandOnPrint
              >
                {renderSteps(completedSteps)}
              </Collapsible>
            </>
          )}
        </div>
      )}
    </div>
  );

  if (renderInline) return content;

  return <div className="StartGuide__card">{content}</div>;
}
